// src/pages/CheckoutPage.jsx
import React, { useState } from 'react';

import { theme } from '../utils/constants';
import { BASE_URL } from '../utils/constants';

const btnPrimary = {
  background: `linear-gradient(90deg, ${theme.accent}, ${theme.accentDark})`,
  color: '#111',
  border: 'none',
  padding: '0.9rem 2.2rem',
  borderRadius: '50px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '1.05rem',
  transition: 'all 0.2s'
};

const btnSecondary = {
  background: 'transparent',
  border: `2px solid ${theme.primary}`,
  color: theme.primary,
  padding: '0.9rem 2.2rem',
  borderRadius: '50px',
  fontWeight: 500,
  cursor: 'pointer',
  fontSize: '1.05rem'
};

const inputStyle = {
  width: '100%',
  padding: '0.95rem',
  marginBottom: '1.2rem',
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  fontSize: '1rem',
  background: 'white'
};

export default function CheckoutPage({ cartItems, getTotalPrice, navigateTo }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState('pickup');
  const [deliveryAddress, setDeliveryAddress] = useState({
    fullName: '',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    additionalInfo: ''
  });
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [operator, setOperator] = useState('airtel');
  const [receiveOrderAlerts, setReceiveOrderAlerts] = useState(true);   // default ON
  const [receiveMarketing, setReceiveMarketing] = useState(false);      // default OFF
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState('');
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [status, setStatus] = useState(null);

  const handleAddressChange = (field, value) => {
    setDeliveryAddress(prev => ({ ...prev, [field]: value }));
  };

  const isDeliveryComplete = () => {
    if (deliveryMethod === 'pickup') return true;
    const { fullName, street, city, province } = deliveryAddress;
    return fullName.trim() && street.trim() && city.trim() && province.trim();
  };

  const initiatePayment = async () => {
    if (!phone || phone.replace(/[^0-9]/g, '').length < 9) {
      alert("Please enter a valid phone number");
      return;
    }

    // Basic email validation only if they typed something
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert("Please enter a valid email address or leave it blank");
      return;
    }

    setLoading(true);

    const items = cartItems.map(item => ({
      name: item.title,
      price: item.price,
      quantity: item.quantity,
      size: item.selectedSize || null,
      color: item.selectedColor || null,
      totalCost: item.price * item.quantity
    }));

    const payload = {
      amount: parseFloat(getTotalPrice()),
      phone_number: phone.replace(/[^0-9]/g, ''),
      operator,
      items,
      delivery_method: deliveryMethod,
      delivery_address: deliveryMethod === 'delivery' ? deliveryAddress : null,
      email: email.trim() || null,                    // optional
      receiveOrderAlerts: receiveOrderAlerts,         // true/false
      receiveMarketing: receiveMarketing              // true/false
    };

    try {
      const response = await fetch(`${BASE_URL}/payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.data?.reference) {
        setReference(data.data.reference);
        setPaymentInitiated(true);
        alert("Payment request sent! Check your phone for the payment prompt.");
      } else {
        alert(data.message || "Payment initiation failed. Try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    if (!reference) return;

    try {
      const response = await fetch(`${BASE_URL}/collections/status/${reference}`);
      const data = await response.json();

      setStatus(data);

      if (data.data?.status === "success") {
        alert("Payment successful! Thank you for your order.");
        navigateTo('home');
      } else if (data.data?.status === "failed") {
        alert("Payment failed. You can try again.");
      } else {
        alert("Payment is still processing. Please wait a moment and check again.");
      }
    } catch (err) {
      console.error(err);
      alert("Could not check payment status. Try again later.");
    }
  };

  const total = getTotalPrice();

  // Toggle switch style
  const Toggle = ({ checked, onChange, label, description }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        padding: '1rem 1.2rem',
        background: checked ? `${theme.primary}10` : '#f8fafc',
        borderRadius: '12px',
        border: `1px solid ${checked ? theme.primary : theme.border}`,
        marginBottom: '1rem',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
      onClick={() => onChange(!checked)}
    >
      <div
        style={{
          width: '48px',
          height: '28px',
          borderRadius: '999px',
          background: checked ? theme.primary : '#cbd5e1',
          position: 'relative',
          transition: 'background 0.2s',
          flexShrink: 0,
          marginTop: '2px'
        }}
      >
        <div
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: 'white',
            position: 'absolute',
            top: '3px',
            left: checked ? '23px' : '3px',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        />
      </div>
      <div>
        <div style={{ fontWeight: 600, color: theme.text }}>{label}</div>
        <div style={{ fontSize: '0.9rem', color: theme.textLight, marginTop: '0.2rem' }}>
          {description}
        </div>
      </div>
    </div>
  );

  return (
    <section style={{ padding: '6rem 1.5rem 10rem', maxWidth: '960px', margin: '0 auto', background: 'white' }}>
      <h2
        style={{
          textAlign: 'center',
          fontSize: '2.8rem',
          marginBottom: '3.5rem',
          fontFamily: "'Roboto Slab', serif",
          color: theme.text
        }}
      >
        Checkout
      </h2>

      {/* Progress Steps */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '4rem',
          gap: '1.5rem',
          flexWrap: 'wrap'
        }}
      >
        {['Cart Review', 'Delivery', 'Payment'].map((label, i) => (
          <React.Fragment key={label}>
            <div style={{ textAlign: 'center', minWidth: '140px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  margin: '0 auto 0.8rem',
                  borderRadius: '50%',
                  background: currentStep > i + 1 ? theme.accent :
                              currentStep === i + 1 ? theme.primary : '#e0e0e0',
                  color: currentStep >= i + 1 ? 'white' : '#666',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 600,
                  fontSize: '1.2rem'
                }}
              >
                {i + 1}
              </div>
              <div
                style={{
                  fontWeight: currentStep === i + 1 ? 600 : 400,
                  color: currentStep === i + 1 ? theme.primary : '#555'
                }}
              >
                {label}
              </div>
            </div>
            {i < 2 && (
              <div
                style={{
                  height: '3px',
                  flex: 1,
                  maxWidth: '120px',
                  background: currentStep > i + 1 ? theme.primary : '#ddd',
                  borderRadius: '2px'
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Cart Review */}
      {currentStep === 1 && (
        <div>
          <h3 style={{ fontSize: '1.9rem', marginBottom: '2rem', color: theme.text }}>
            Review Your Items
          </h3>

          {cartItems.length === 0 ? (
            <p style={{ textAlign: 'center', color: theme.textLight, fontSize: '1.2rem' }}>
              Your cart is empty
            </p>
          ) : (
            <>
              {cartItems.map(item => (
                <div
                  key={item.variantId || item.id}
                  style={{
                    display: 'flex',
                    gap: '1.5rem',
                    padding: '1.2rem',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '12px',
                    marginBottom: '1.2rem',
                    alignItems: 'center',
                    background: '#faf9ff'
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{ width: '100px', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.4rem', fontSize: '1.25rem' }}>{item.title}</h4>
                    {(item.selectedSize || item.selectedColor) && (
                      <p style={{ color: theme.textLight, margin: '0.3rem 0' }}>
                        {item.selectedSize && `Size: ${item.selectedSize}`}
                        {item.selectedSize && item.selectedColor && ' • '}
                        {item.selectedColor && `Color: ${item.selectedColor}`}
                      </p>
                    )}
                    <p style={{ color: theme.primary, fontWeight: 600, margin: '0.5rem 0' }}>
                      zmk{item.price.toFixed(2)} × {item.quantity} = zmk{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}

              <div
                style={{
                  textAlign: 'right',
                  padding: '1.5rem',
                  background: theme.bgLight,
                  borderRadius: '12px',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  margin: '2rem 0',
                  color: theme.primary
                }}
              >
                Total: zmk {total}
              </div>

              <div style={{ textAlign: 'right' }}>
                <button onClick={() => setCurrentStep(2)} style={btnPrimary}>
                  Continue to Delivery
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 2: Delivery */}
      {currentStep === 2 && (
        <div>
          <h3 style={{ fontSize: '1.9rem', marginBottom: '2rem', color: theme.text }}>
            Delivery Method
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem',
              marginBottom: '3rem'
            }}
          >
            <div
              onClick={() => setDeliveryMethod('pickup')}
              style={{
                padding: '1.6rem',
                border: deliveryMethod === 'pickup' ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`,
                borderRadius: '12px',
                background: deliveryMethod === 'pickup' ? `${theme.primary}08` : 'white',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              <strong style={{ fontSize: '1.15rem' }}>Store Pickup</strong>
              <p style={{ margin: '0.6rem 0 0', color: theme.textLight }}>
                Collect from UPENDO STORE location
              </p>
            </div>

            <div
              onClick={() => setDeliveryMethod('delivery')}
              style={{
                padding: '1.6rem',
                border: deliveryMethod === 'delivery' ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`,
                borderRadius: '12px',
                background: deliveryMethod === 'delivery' ? `${theme.primary}08` : 'white',
                cursor: 'pointer',
                textAlign: 'center'
              }}
            >
              <strong style={{ fontSize: '1.15rem' }}>Home Delivery</strong>
              <p style={{ margin: '0.6rem 0 0', color: theme.textLight }}>
                We'll deliver to your address
              </p>
            </div>
          </div>

          {deliveryMethod === 'delivery' && (
            <div
              style={{
                background: theme.bgLight,
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2.5rem'
              }}
            >
              <h4 style={{ marginBottom: '1.5rem', fontSize: '1.3rem' }}>Delivery Address</h4>

              <input
                type="text"
                placeholder="Full Name *"
                value={deliveryAddress.fullName}
                onChange={e => handleAddressChange('fullName', e.target.value)}
                style={inputStyle}
              />
              <input
                type="text"
                placeholder="Street Address *"
                value={deliveryAddress.street}
                onChange={e => handleAddressChange('street', e.target.value)}
                style={inputStyle}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <input
                  type="text"
                  placeholder="City *"
                  value={deliveryAddress.city}
                  onChange={e => handleAddressChange('city', e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Province *"
                  value={deliveryAddress.province}
                  onChange={e => handleAddressChange('province', e.target.value)}
                  style={inputStyle}
                />
              </div>
              <input
                type="text"
                placeholder="Postal Code (optional)"
                value={deliveryAddress.postalCode}
                onChange={e => handleAddressChange('postalCode', e.target.value)}
                style={inputStyle}
              />
              <textarea
                placeholder="Additional info (apt, landmark...)"
                value={deliveryAddress.additionalInfo}
                onChange={e => handleAddressChange('additionalInfo', e.target.value)}
                style={{ ...inputStyle, height: '100px', resize: 'vertical' }}
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <button onClick={() => setCurrentStep(1)} style={btnSecondary}>← Back</button>
            <button
              onClick={() => setCurrentStep(3)}
              disabled={!isDeliveryComplete()}
              style={{
                ...btnPrimary,
                opacity: isDeliveryComplete() ? 1 : 0.5,
                cursor: isDeliveryComplete() ? 'pointer' : 'not-allowed'
              }}
            >
              Continue to Payment →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Payment */}
      {currentStep === 3 && (
        <div style={{ maxWidth: '540px', margin: '0 auto' }}>
          <h3 style={{ fontSize: '1.9rem', marginBottom: '2.2rem', textAlign: 'center' }}>
            Mobile Money Payment
          </h3>

          {!paymentInitiated ? (
            <>
              <div style={{ marginBottom: '1.8rem' }}>
                <label style={{ display: 'block', marginBottom: '0.7rem', fontWeight: 500 }}>
                  Mobile Operator
                </label>
                <select value={operator} onChange={e => setOperator(e.target.value)} style={inputStyle}>
                  <option value="airtel">Airtel Money</option>
                  <option value="mtn">MTN Mobile Money</option>
                </select>
              </div>

              <div style={{ marginBottom: '1.8rem' }}>
                <label style={{ display: 'block', marginBottom: '0.7rem', fontWeight: 500 }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  placeholder="e.g. 0971234567"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Email + Preferences */}
              <div style={{ marginBottom: '1.8rem' }}>
                <label style={{ display: 'block', marginBottom: '0.7rem', fontWeight: 500 }}>
                  Email Address <span style={{ color: theme.textLight, fontWeight: 400 }}>(optional)</span>
                </label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={inputStyle}
                />
                <p style={{ fontSize: '0.85rem', color: theme.textLight, marginTop: '-0.6rem' }}>
                  We’ll only use this if you turn on the options below
                </p>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <Toggle
                  checked={receiveOrderAlerts}
                  onChange={setReceiveOrderAlerts}
                  label="Order updates via email"
                  description="Get notified when payment is confirmed, shipped, or delivered"
                />
                <Toggle
                  checked={receiveMarketing}
                  onChange={setReceiveMarketing}
                  label="Marketing & promotions"
                  description="Occasional emails about new products, discounts and offers"
                />
              </div>

              <div
                style={{
                  background: theme.bgLight,
                  padding: '1.8rem',
                  borderRadius: '12px',
                  textAlign: 'center',
                  fontSize: '1.6rem',
                  fontWeight: 700,
                  marginBottom: '2.5rem',
                  color: theme.primary
                }}
              >
                Total to Pay: zmk {total}
              </div>

              <button
                onClick={initiatePayment}
                disabled={loading || !phone.trim()}
                style={{
                  ...btnPrimary,
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: '1.15rem',
                  opacity: loading || !phone.trim() ? 0.6 : 1,
                  cursor: loading || !phone.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Processing...' : `Pay ${total} via ${operator.toUpperCase()}`}
              </button>
            </>
          ) : (
            <div
              style={{
                background: '#f0fdf4',
                padding: '3rem 1.5rem',
                borderRadius: '16px',
                border: `2px dashed ${theme.primary}`,
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  background: theme.primary,
                  color: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem',
                  margin: '0 auto 1.5rem'
                }}
              >
                ✓
              </div>

              <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>Payment Request Sent!</h3>
              <p style={{ color: theme.textLight, marginBottom: '2rem', lineHeight: 1.6 }}>
                Please check your phone and approve the payment request.
              </p>

              <div
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '10px',
                  marginBottom: '2rem',
                  fontFamily: 'monospace',
                  fontSize: '1.3rem',
                  color: theme.primary,
                  wordBreak: 'break-all'
                }}
              >
                Ref: {reference}
              </div>

              <button
                onClick={checkPaymentStatus}
                style={{ ...btnPrimary, padding: '1rem 2.5rem', fontSize: '1.1rem' }}
              >
                Check Payment Status
              </button>

              {status && (
                <div
                  style={{
                    marginTop: '2rem',
                    padding: '1.2rem',
                    background: status.data?.status === 'success' ? '#d4edda' : '#f8d7da',
                    borderRadius: '10px',
                    color: status.data?.status === 'success' ? '#155724' : '#721c24'
                  }}
                >
                  Status: <strong>{status.data?.status?.toUpperCase()}</strong><br />
                  {status.message || 'No additional message'}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <button onClick={() => setCurrentStep(2)} style={btnSecondary}>← Back</button>
          </div>
        </div>
      )}
    </section>
  );
}


// // src/pages/CheckoutPage.jsx
// import React, { useState } from 'react';

// import { theme } from '../utils/constants';
// import { BASE_URL } from '../utils/constants';

// const btnPrimary = {
//   background: `linear-gradient(90deg, ${theme.accent}, ${theme.accentDark})`,
//   color: '#111',
//   border: 'none',
//   padding: '0.9rem 2.2rem',
//   borderRadius: '50px',
//   fontWeight: 600,
//   cursor: 'pointer',
//   fontSize: '1.05rem',
//   transition: 'all 0.2s'
// };

// const btnSecondary = {
//   background: 'transparent',
//   border: `2px solid ${theme.primary}`,
//   color: theme.primary,
//   padding: '0.9rem 2.2rem',
//   borderRadius: '50px',
//   fontWeight: 500,
//   cursor: 'pointer',
//   fontSize: '1.05rem'
// };

// const inputStyle = {
//   width: '100%',
//   padding: '0.95rem',
//   marginBottom: '1.2rem',
//   border: `1px solid ${theme.border}`,
//   borderRadius: '10px',
//   fontSize: '1rem',
//   background: 'white'
// };

// export default function CheckoutPage({ cartItems, getTotalPrice, navigateTo }) {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [deliveryMethod, setDeliveryMethod] = useState('pickup');
//   const [deliveryAddress, setDeliveryAddress] = useState({
//     fullName: '',
//     street: '',
//     city: '',
//     province: '',
//     postalCode: '',
//     additionalInfo: ''
//   });
//   const [phone, setPhone] = useState('');
//   const [operator, setOperator] = useState('airtel');
//   const [loading, setLoading] = useState(false);
//   const [reference, setReference] = useState('');
//   const [paymentInitiated, setPaymentInitiated] = useState(false);
//   const [status, setStatus] = useState(null);

//   const handleAddressChange = (field, value) => {
//     setDeliveryAddress(prev => ({ ...prev, [field]: value }));
//   };

//   const isDeliveryComplete = () => {
//     if (deliveryMethod === 'pickup') return true;
//     const { fullName, street, city, province } = deliveryAddress;
//     return fullName.trim() && street.trim() && city.trim() && province.trim();
//   };

//   const initiatePayment = async () => {
//     if (!phone || phone.replace(/[^0-9]/g, '').length < 9) {
//       alert("Please enter a valid phone number");
//       return;
//     }

//     setLoading(true);

//     const items = cartItems.map(item => ({
//       name: item.title,
//       price: item.price,
//       quantity: item.quantity,
//       size: item.selectedSize || null,
//       color: item.selectedColor || null,
//       totalCost: item.price * item.quantity
//     }));

//     const payload = {
//       amount: parseFloat(getTotalPrice()),
//       phone_number: phone.replace(/[^0-9]/g, ''),
//       operator,
//       items,
//       delivery_method: deliveryMethod,
//       delivery_address: deliveryMethod === 'delivery' ? deliveryAddress : null
//     };

//     try {
//       const response = await fetch(`${BASE_URL}/payment`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload)
//       });

//       const data = await response.json();

//       if (response.ok && data.data?.reference) {
//         setReference(data.data.reference);
//         setPaymentInitiated(true);
//         alert("Payment request sent! Check your phone for the payment prompt.");
//       } else {
//         alert(data.message || "Payment initiation failed. Try again.");
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Network error. Please check your connection and try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const checkPaymentStatus = async () => {
//     if (!reference) return;

//     try {
//       const response = await fetch(`${BASE_URL}/collections/status/${reference}`);
//       const data = await response.json();

//       setStatus(data);

//       if (data.data?.status === "success") {
//         alert("Payment successful! Thank you for your order.");
//         navigateTo('home');
//       } else if (data.data?.status === "failed") {
//         alert("Payment failed. You can try again.");
//       } else {
//         alert("Payment is still processing. Please wait a moment and check again.");
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Could not check payment status. Try again later.");
//     }
//   };

//   const total = getTotalPrice();

//   return (
//     <section style={{ padding: '6rem 1.5rem 10rem', maxWidth: '960px', margin: '0 auto', background: 'white' }}>
//       <h2
//         style={{
//           textAlign: 'center',
//           fontSize: '2.8rem',
//           marginBottom: '3.5rem',
//           fontFamily: "'Roboto Slab', serif",
//           color: theme.text
//         }}
//       >
//         Checkout
//       </h2>

//       {/* Progress Steps */}
//       <div
//         style={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           marginBottom: '4rem',
//           gap: '1.5rem',
//           flexWrap: 'wrap'
//         }}
//       >
//         {['Cart Review', 'Delivery', 'Payment'].map((label, i) => (
//           <React.Fragment key={label}>
//             <div style={{ textAlign: 'center', minWidth: '140px' }}>
//               <div
//                 style={{
//                   width: '48px',
//                   height: '48px',
//                   margin: '0 auto 0.8rem',
//                   borderRadius: '50%',
//                   background: currentStep > i + 1 ? theme.accent :
//                               currentStep === i + 1 ? theme.primary : '#e0e0e0',
//                   color: currentStep >= i + 1 ? 'white' : '#666',
//                   display: 'grid',
//                   placeItems: 'center',
//                   fontWeight: 600,
//                   fontSize: '1.2rem'
//                 }}
//               >
//                 {i + 1}
//               </div>
//               <div
//                 style={{
//                   fontWeight: currentStep === i + 1 ? 600 : 400,
//                   color: currentStep === i + 1 ? theme.primary : '#555'
//                 }}
//               >
//                 {label}
//               </div>
//             </div>
//             {i < 2 && (
//               <div
//                 style={{
//                   height: '3px',
//                   flex: 1,
//                   maxWidth: '120px',
//                   background: currentStep > i + 1 ? theme.primary : '#ddd',
//                   borderRadius: '2px'
//                 }}
//               />
//             )}
//           </React.Fragment>
//         ))}
//       </div>

//       {/* Step 1: Cart Review */}
//       {currentStep === 1 && (
//         <div>
//           <h3 style={{ fontSize: '1.9rem', marginBottom: '2rem', color: theme.text }}>
//             Review Your Items
//           </h3>

//           {cartItems.length === 0 ? (
//             <p style={{ textAlign: 'center', color: theme.textLight, fontSize: '1.2rem' }}>
//               Your cart is empty
//             </p>
//           ) : (
//             <>
//               {cartItems.map(item => (
//                 <div
//                   key={item.variantId || item.id}
//                   style={{
//                     display: 'flex',
//                     gap: '1.5rem',
//                     padding: '1.2rem',
//                     border: `1px solid ${theme.border}`,
//                     borderRadius: '12px',
//                     marginBottom: '1.2rem',
//                     alignItems: 'center',
//                     background: '#faf9ff'
//                   }}
//                 >
//                   <img
//                     src={item.image}
//                     alt={item.title}
//                     style={{ width: '100px', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
//                   />
//                   <div style={{ flex: 1 }}>
//                     <h4 style={{ margin: '0 0 0.4rem', fontSize: '1.25rem' }}>{item.title}</h4>
//                     {(item.selectedSize || item.selectedColor) && (
//                       <p style={{ color: theme.textLight, margin: '0.3rem 0' }}>
//                         {item.selectedSize && `Size: ${item.selectedSize}`}
//                         {item.selectedSize && item.selectedColor && ' • '}
//                         {item.selectedColor && `Color: ${item.selectedColor}`}
//                       </p>
//                     )}
//                     <p style={{ color: theme.primary, fontWeight: 600, margin: '0.5rem 0' }}>
//                       zmk{item.price.toFixed(2)} × {item.quantity} = zmk{(item.price * item.quantity).toFixed(2)}
//                     </p>
//                   </div>
//                 </div>
//               ))}

//               <div
//                 style={{
//                   textAlign: 'right',
//                   padding: '1.5rem',
//                   background: theme.bgLight,
//                   borderRadius: '12px',
//                   fontSize: '1.5rem',
//                   fontWeight: 700,
//                   margin: '2rem 0',
//                   color: theme.primary
//                 }}
//               >
//                 Total: zmk {total}
//               </div>

//               <div style={{ textAlign: 'right' }}>
//                 <button onClick={() => setCurrentStep(2)} style={btnPrimary}>
//                   Continue to Delivery
//                 </button>
//               </div>
//             </>
//           )}
//         </div>
//       )}

//       {/* Step 2: Delivery */}
//       {currentStep === 2 && (
//         <div>
//           <h3 style={{ fontSize: '1.9rem', marginBottom: '2rem', color: theme.text }}>
//             Delivery Method
//           </h3>

//           <div
//             style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
//               gap: '1.5rem',
//               marginBottom: '3rem'
//             }}
//           >
//             <div
//               onClick={() => setDeliveryMethod('pickup')}
//               style={{
//                 padding: '1.6rem',
//                 border: deliveryMethod === 'pickup' ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`,
//                 borderRadius: '12px',
//                 background: deliveryMethod === 'pickup' ? `${theme.primary}08` : 'white',
//                 cursor: 'pointer',
//                 textAlign: 'center'
//               }}
//             >
//               <strong style={{ fontSize: '1.15rem' }}>Store Pickup</strong>
//               <p style={{ margin: '0.6rem 0 0', color: theme.textLight }}>
//                 Collect from UPENDO STORE location
//               </p>
//             </div>

//             <div
//               onClick={() => setDeliveryMethod('delivery')}
//               style={{
//                 padding: '1.6rem',
//                 border: deliveryMethod === 'delivery' ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`,
//                 borderRadius: '12px',
//                 background: deliveryMethod === 'delivery' ? `${theme.primary}08` : 'white',
//                 cursor: 'pointer',
//                 textAlign: 'center'
//               }}
//             >
//               <strong style={{ fontSize: '1.15rem' }}>Home Delivery</strong>
//               <p style={{ margin: '0.6rem 0 0', color: theme.textLight }}>
//                 We'll deliver to your address
//               </p>
//             </div>
//           </div>

//           {deliveryMethod === 'delivery' && (
//             <div
//               style={{
//                 background: theme.bgLight,
//                 padding: '2rem',
//                 borderRadius: '12px',
//                 marginBottom: '2.5rem'
//               }}
//             >
//               <h4 style={{ marginBottom: '1.5rem', fontSize: '1.3rem' }}>Delivery Address</h4>

//               <input
//                 type="text"
//                 placeholder="Full Name *"
//                 value={deliveryAddress.fullName}
//                 onChange={e => handleAddressChange('fullName', e.target.value)}
//                 style={inputStyle}
//               />
//               <input
//                 type="text"
//                 placeholder="Street Address *"
//                 value={deliveryAddress.street}
//                 onChange={e => handleAddressChange('street', e.target.value)}
//                 style={inputStyle}
//               />
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
//                 <input
//                   type="text"
//                   placeholder="City *"
//                   value={deliveryAddress.city}
//                   onChange={e => handleAddressChange('city', e.target.value)}
//                   style={inputStyle}
//                 />
//                 <input
//                   type="text"
//                   placeholder="Province *"
//                   value={deliveryAddress.province}
//                   onChange={e => handleAddressChange('province', e.target.value)}
//                   style={inputStyle}
//                 />
//               </div>
//               <input
//                 type="text"
//                 placeholder="Postal Code (optional)"
//                 value={deliveryAddress.postalCode}
//                 onChange={e => handleAddressChange('postalCode', e.target.value)}
//                 style={inputStyle}
//               />
//               <textarea
//                 placeholder="Additional info (apt, landmark...)"
//                 value={deliveryAddress.additionalInfo}
//                 onChange={e => handleAddressChange('additionalInfo', e.target.value)}
//                 style={{ ...inputStyle, height: '100px', resize: 'vertical' }}
//               />
//             </div>
//           )}

//           <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
//             <button onClick={() => setCurrentStep(1)} style={btnSecondary}>← Back</button>
//             <button
//               onClick={() => setCurrentStep(3)}
//               disabled={!isDeliveryComplete()}
//               style={{
//                 ...btnPrimary,
//                 opacity: isDeliveryComplete() ? 1 : 0.5,
//                 cursor: isDeliveryComplete() ? 'pointer' : 'not-allowed'
//               }}
//             >
//               Continue to Payment →
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Step 3: Payment */}
//       {currentStep === 3 && (
//         <div style={{ maxWidth: '540px', margin: '0 auto' }}>
//           <h3 style={{ fontSize: '1.9rem', marginBottom: '2.2rem', textAlign: 'center' }}>
//             Mobile Money Payment
//           </h3>

//           {!paymentInitiated ? (
//             <>
//               <div style={{ marginBottom: '1.8rem' }}>
//                 <label style={{ display: 'block', marginBottom: '0.7rem', fontWeight: 500 }}>
//                   Mobile Operator
//                 </label>
//                 <select value={operator} onChange={e => setOperator(e.target.value)} style={inputStyle}>
//                   <option value="airtel">Airtel Money</option>
//                   <option value="mtn">MTN Mobile Money</option>
//                 </select>
//               </div>

//               <div style={{ marginBottom: '2.2rem' }}>
//                 <label style={{ display: 'block', marginBottom: '0.7rem', fontWeight: 500 }}>
//                   Phone Number
//                 </label>
//                 <input
//                   type="tel"
//                   placeholder="e.g. 0971234567"
//                   value={phone}
//                   onChange={e => setPhone(e.target.value)}
//                   style={inputStyle}
//                 />
//               </div>

//               <div
//                 style={{
//                   background: theme.bgLight,
//                   padding: '1.8rem',
//                   borderRadius: '12px',
//                   textAlign: 'center',
//                   fontSize: '1.6rem',
//                   fontWeight: 700,
//                   marginBottom: '2.5rem',
//                   color: theme.primary
//                 }}
//               >
//                 Total to Pay: zmk {total}
//               </div>

//               <button
//                 onClick={initiatePayment}
//                 disabled={loading || !phone.trim()}
//                 style={{
//                   ...btnPrimary,
//                   width: '100%',
//                   padding: '1.2rem',
//                   fontSize: '1.15rem',
//                   opacity: loading || !phone.trim() ? 0.6 : 1,
//                   cursor: loading || !phone.trim() ? 'not-allowed' : 'pointer'
//                 }}
//               >
//                 {loading ? 'Processing...' : `Pay ${total} via ${operator.toUpperCase()}`}
//               </button>
//             </>
//           ) : (
//             <div
//               style={{
//                 background: '#f0fdf4',
//                 padding: '3rem 1.5rem',
//                 borderRadius: '16px',
//                 border: `2px dashed ${theme.primary}`,
//                 textAlign: 'center'
//               }}
//             >
//               <div
//                 style={{
//                   width: '80px',
//                   height: '80px',
//                   background: theme.primary,
//                   color: 'white',
//                   borderRadius: '50%',
//                   display: 'flex',
//                   alignItems: 'center',
//                   justifyContent: 'center',
//                   fontSize: '2.5rem',
//                   margin: '0 auto 1.5rem'
//                 }}
//               >
//                 ✓
//               </div>

//               <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>Payment Request Sent!</h3>
//               <p style={{ color: theme.textLight, marginBottom: '2rem', lineHeight: 1.6 }}>
//                 Please check your phone and approve the payment request.
//               </p>

//               <div
//                 style={{
//                   background: 'white',
//                   padding: '1.5rem',
//                   borderRadius: '10px',
//                   marginBottom: '2rem',
//                   fontFamily: 'monospace',
//                   fontSize: '1.3rem',
//                   color: theme.primary,
//                   wordBreak: 'break-all'
//                 }}
//               >
//                 Ref: {reference}
//               </div>

//               <button
//                 onClick={checkPaymentStatus}
//                 style={{ ...btnPrimary, padding: '1rem 2.5rem', fontSize: '1.1rem' }}
//               >
//                 Check Payment Status
//               </button>

//               {status && (
//                 <div
//                   style={{
//                     marginTop: '2rem',
//                     padding: '1.2rem',
//                     background: status.data?.status === 'success' ? '#d4edda' : '#f8d7da',
//                     borderRadius: '10px',
//                     color: status.data?.status === 'success' ? '#155724' : '#721c24'
//                   }}
//                 >
//                   Status: <strong>{status.data?.status?.toUpperCase()}</strong><br />
//                   {status.message || 'No additional message'}
//                 </div>
//               )}
//             </div>
//           )}

//           <div style={{ marginTop: '3rem', textAlign: 'center' }}>
//             <button onClick={() => setCurrentStep(2)} style={btnSecondary}>← Back</button>
//           </div>
//         </div>
//       )}
//     </section>
//   );
// }